import React, { useState } from 'react';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import Icon from '../../../components/AppIcon';

const ContactForm = ({ onSubmit }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [messageCount, setMessageCount] = useState(0);

  const subjectOptions = [
    { value: 'general', label: 'General Inquiry' },
    { value: 'technical', label: 'Technical Support' },
    { value: 'business', label: 'Business Partnership' },
    { value: 'billing', label: 'Billing & Payments' },
    { value: 'feedback', label: 'Feedback & Suggestions' }
  ];

  const validateForm = () => {
    const newErrors = {};

    if (!formData?.name?.trim()) {
      newErrors.name = 'Name is required';
    }

    if (!formData?.email?.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/?.test(formData?.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (!formData?.subject) {
      newErrors.subject = 'Please select a subject';
    }

    if (!formData?.message?.trim()) {
      newErrors.message = 'Message is required';
    } else if (formData?.message?.trim()?.length < 10) {
      newErrors.message = 'Message must be at least 10 characters long';
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    if (field === 'message') {
      setMessageCount(value?.length);
    }

    // Clear error when user starts typing
    if (errors?.[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      // Simulate form submission
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      const referenceNumber = `WF-${Date.now()}`;
      onSubmit({ ...formData, referenceNumber });
      
      // Reset form
      setFormData({
        name: '',
        email: '',
        subject: '',
        message: ''
      });
      setMessageCount(0);
    } catch (error) {
      console.error('Form submission error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="bg-card rounded-2xl shadow-elevation p-8">
      <div className="mb-8">
        <h2 className="text-2xl font-semibold text-foreground mb-2">
          Send us a message
        </h2>
        <p className="text-muted-foreground">
          We'd love to hear from you. Send us a message and we'll respond as soon as possible.
        </p>
      </div>
      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="grid md:grid-cols-2 gap-6">
          <Input
            label="Full Name"
            type="text"
            placeholder="Enter your full name"
            value={formData?.name}
            onChange={(e) => handleInputChange('name', e?.target?.value)}
            error={errors?.name}
            required
          />

          <Input
            label="Email Address"
            type="email"
            placeholder="your.email@example.com"
            value={formData?.email}
            onChange={(e) => handleInputChange('email', e?.target?.value)}
            error={errors?.email}
            required
          />
        </div>

        <Select
          label="Subject"
          placeholder="Select inquiry type"
          options={subjectOptions}
          value={formData?.subject}
          onChange={(value) => handleInputChange('subject', value)}
          error={errors?.subject}
          required
        />

        <div className="space-y-2">
          <label className="block text-sm font-medium text-foreground">
            Message <span className="text-destructive">*</span>
          </label>
          <div className="relative">
            <textarea
              placeholder="Tell us how we can help you..."
              value={formData?.message}
              onChange={(e) => handleInputChange('message', e?.target?.value)}
              rows={6}
              maxLength={1000}
              className={`w-full px-4 py-3 rounded-lg border transition-colors resize-none ${
                errors?.message
                  ? 'border-destructive focus:border-destructive focus:ring-destructive/20' :'border-border focus:border-primary focus:ring-primary/20'
              } focus:outline-none focus:ring-2`}
            />
            <div className="absolute bottom-3 right-3 text-xs text-muted-foreground">
              {messageCount}/1000
            </div>
          </div>
          {errors?.message && (
            <p className="text-sm text-destructive">{errors?.message}</p>
          )}
        </div>

        <div className="bg-muted/30 rounded-lg p-4 border border-muted">
          <div className="flex items-start space-x-3">
            <Icon name="Shield" size={20} className="text-primary mt-0.5" />
            <div className="text-sm">
              <div className="font-medium text-foreground mb-1">
                GDPR Compliant
              </div>
              <div className="text-muted-foreground">
                Your personal information is handled in accordance with GDPR regulations. 
                We only use your data to respond to your inquiry and will not share it with third parties.
              </div>
            </div>
          </div>
        </div>

        <Button
          type="submit"
          loading={isSubmitting}
          iconName="Send"
          iconPosition="right"
          fullWidth
          className="mt-8"
        >
          {isSubmitting ? 'Sending Message...' : 'Send Message'}
        </Button>
      </form>
    </div>
  );
};

export default ContactForm;